<template>
  <div>
    <b-row>
      <b-col>
        <form ref="form"
              name="search"
              method="post"
              @submit.prevent="find()"
        >
          <sdui-search
            id="postalcode"
            ref="schoolSearchInput"
            v-model="input_val"
            type="search"
            :debounce="500"
            name="postalcode"
            :input-label-sr-only-text="$t('public.school_search.title')"
            :minlength="minLength"
            :loading="$wait.waiting('find school')"
            :placeholder="$t('public.school_search.placeholder')"
            autocomplete="off"
            @submit="event.preventDefault()"
            @update="find"
          />
        </form>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <!-- Display instructions before user starts search -->
        <div v-if="showInstructions" class="instructions-wrapper d-block p-4">
          <div>
            <p class="title font-weight-bold mb-0">{{ $t('public.school_search.instructions.part1.title') }}</p>
            <p class="text-muted mb-0">
              <span class="overwritten"> {{ $t('public.school_search.instructions.part1.example1') }} </span>
              <sdui-icon class="icon-arrow-o" /> {{ $t('public.school_search.instructions.part1.example2') }}
            </p>
          </div>
          <p class="font-weight-bold mb-0 pt-4 pb-4">{{ $t('public.school_search.instructions.separator') }}</p>
          <div>
            <p class="title font-weight-bold mb-0">{{ $t('public.school_search.instructions.part2.title') }}</p>
            <p class="mb-0 text-muted">{{ $t('public.school_search.instructions.part2.example') }}</p>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols=""
             role="region"
             aria-live="polite"
      >
        <ul class="list-unstyled">
          <li v-for="school in schools" :key="school.id">
            <b-button
              variant="transparent"
              block
              class="btn card text-left mb-4 school-card aux-border-radius"
              :data-cy="`school-search-result-${school.name}`"
              :data-title="school.name"
              @click="$emit('click', school)"
            >
              <b-row align-v="center">
                <b-col cols="12">
                  <div class="p-2 pr-4">
                    <div class="mb-0 cell-badge-wrapper">
                      <span class="sr-only">{{ $t('accessibility.sign_in_at') }}</span>

                      {{ school.name }}

                      <span v-if="school.is_beta"
                            class="cell-badge secondary-bg nano"
                            lang="en"
                      >{{ $t('global.beta') }}</span>
                    </div>
                    <div v-if="school.is_partner">
                      <sdui-icon class="icon-star-o text-primary" />
                      <span class="text-primary position-relative aux-top">{{ $t('public.school_search.partner_school') }}</span>
                    </div>
                    <span v-if="school.city" class="text-muted">{{ school.city }}</span>
                    <span v-if="accessories" class="gray-100"> | S: {{ school.id }}</span>
                  </div>
                </b-col>

                <b-col cols="" class="abs-icon">
                  <sdui-icon class="icon-chevron-right gray-100 small float-right" />
                </b-col>
              </b-row>
            </b-button>
          </li>
        </ul>

        <div v-if="searchTriggered && !accessories" class="text-center mt-5">
          <sdui-icon class="sdui-icon icon-info-o icon-4x gray-300" />
          <h3 class="title font-weight-bold mt-4">{{ $t('public.school_search.not_found.title') }}</h3>
          <p class="text-muted" v-html="$sanitize($t('public.school_search.not_found.description'))" />
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import { School } from 'src/store/models/index.js'
import isMobileDeviceMixing from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import SduiSearch from 'src/components/miscellaneous/ui/forms/SduiSearch.vue'
import Leads from 'src/api/leads.js'
import Core from 'src/api/core.js'
import brandingMixin from 'src/mixins/branding/brandingMixin.js'

export default {
  name: 'SchoolSearch',
  components: { SduiSearch },
  mixins: [isMobileDeviceMixing, brandingMixin],
  props: {
    accessories: {
      type: Boolean,
      default: false,
    },

    showUnregisteredSchools: {
      type: Boolean,
      default: true,
    },

  },

  data: () => ({
    input_val: '',
    schools: [],
    isLoading: false,
    minLength: 3,
    searchTriggered: false,
    showInfoModal: false,
    currentSearchHasFinished: true,
  }),

  computed: {
    input_url() {
      return this.$route.query.keyword
    },

    emailLink() {
      const mailTo = 'info@sdui.de'
      const subject = this.$t('public.school_search.email.subject')
      const body = this.$t('public.school_search.email.body')

      return `mailto:${mailTo}?subject=${subject}&body=${body}`
    },

    showInstructions() {
      return !this.schools?.length && !this.searchTriggered && !this.isLoading && !this.accessories
    },
  },

  emits: ['click'],

  methods: {
    find: waitFor('find school', async function (searchVal) {
      const searchValue = searchVal || this.input_val

      if (searchValue.length < this.minLength) {
        this.schools = []
        // reset schoolsearch if input is null again
        this.searchTriggered = this.searchTriggered && !!searchValue.length
        return
      }
      // reset current search
      this.currentSearchHasFinished = false
      const localSearch = searchValue

      this.isLoading = true

      if (this.$route.query.keyword !== searchValue) {
        // Set URL parameter:
        this.$router.replace({ query: { keyword: searchValue } })
      }

      // Dispatch:
      const res = this.showUnregisteredSchools
        ? await Leads.getLeads({
          params: {
            search: searchValue,
            ...this.brandingConfig.search.queries,
          },
        })
        : await Core.getFindSchools({ params: { keyword: searchValue } })

      // ignore all requests after the current search term is searched
      if (this.currentSearchHasFinished) return

      School.insertOrUpdate({ data: res.data })
      this.schools = res.data

      this.isLoading = false
      this.searchTriggered = true

      if (localSearch !== searchValue) return

      // reset search for all searches that come in later than the current search term
      this.currentSearchHasFinished = true
    }),
  },

  watch: {
    input_url: {
      handler(newValue) {
        this.input_val = newValue || ''
        this.input_val && this.find()
      },

      immediate: true,
    },
  },
}
</script>

<style scoped lang="scss">
.aux-border-radius {
  border-radius: 17px;
}

.aux-top {
  top: 1px;
}

.cell-badge-wrapper {
  display: flex;
  align-items: flex-end;

  .cell-badge {
    padding: 2px 7px;
    border-radius: 30px;
    margin-left: 5px;
  }
}

.primary-shadow {
  box-shadow: 0 10px 20px -10px $color-primary;
}

.school-card.btn.card:active {
  background-color: $color-secondary;
}

.abs-icon {
  position: absolute;
  left: 0;
}

.icon-4x {
  font-size: pxtorem(64);
}

.instructions-wrapper {
  border: 1px solid $color-gray-100;
  border-radius: 5px;
  font-size: pxtorem(14);
  background-color: $color-white;

  .icon-arrow-o {
    font-size: pxtorem(12);
    display: inline-block;
    vertical-align: middle;
    position: relative;
    margin: 0 5px;
    transform: translateY(-2px);
  }

  // styles for arrow up
  &::after,
  &::before {
    content: ' ';
    bottom: calc(100% - 1px);
    left: 55px;
    border: solid transparent;
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }
  &::after {
    border-color: rgba($color-white-rgb, 0);
    border-bottom-color: $color-white;
    border-width: 15px;
    margin-left: -15px;
  }
  &::before {
    border-color: rgba($color-gray-100-rgb, 0);
    border-bottom-color: $color-gray-100;
    border-width: 16px;
    margin-left: -16px;
  }
}
</style>
